/**
 * Batch Genealogy — Lineage Table.
 * Same lineage as the graph, tabular: Produced Batch (with stage badge),
 * Consumed From (Parents), Yield, Qty, Status, MFG Date.
 */
import React from 'react';
import {
  Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, makeStyles,
} from '@material-ui/core';
import { STATUS } from '../statusConfig';

const useStyles = makeStyles((theme) => ({
  // The table scrolls inside itself (rather than growing the page) so the
  // header row stays visible while paging through a long lineage — same
  // behaviour as the project tab tables. Height is owned by the parent
  // `viewRegion` (index.jsx) so the graph and table share one bounded,
  // single-scroll area; the table simply fills it.
  container: {
    border: '1px solid #e5e7eb',
    borderRadius: 10,
    overflowX: 'auto',
    overflowY: 'auto',
    height: '100%',
  },
  // Sticky declared here (not left to MUI's stickyHeader default) so the opaque
  // #F9F9FC wins over the theme background and rows don't show through.
  headerCell: {
    fontWeight: 700,
    color: '#000000',
    background: '#F9F9FC',
    fontSize: 13,
    whiteSpace: 'nowrap',
    position: 'sticky',
    top: 0,
    zIndex: 2,
    borderBottom: '1px solid #e5e7eb',
  },
  cell: { fontSize: 13, color: '#333333', fontVariantNumeric: 'tabular-nums' },
  statusChip: { display: 'inline-block', fontSize: 12, fontWeight: 600, borderRadius: 12, padding: '3px 10px' },
  stepBadge: {
    width: 22, height: 22, borderRadius: '50%', background: '#eef2f7', color: '#000000',
    fontSize: 12, fontWeight: 700, display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
  },
}));

const LineageTable = ({ nodes, steps = [], onSelectBatch }) => {
  const classes = useStyles();
  // Order rows by the API-provided step order (final stage first) — no hardcoded
  // stage ranking. `steps` is the same ordered legend the graph renders.
  const rank = {};
  steps.forEach((s, i) => { rank[s.id] = i; });
  const rankOf = (step) => (rank[step] != null ? rank[step] : Number.MAX_SAFE_INTEGER);
  const rows = [...nodes].sort(
    (a, b) => rankOf(a.step) - rankOf(b.step) || a.id.localeCompare(b.id)
  );

  return (
    <TableContainer component={Paper} elevation={0} className={classes.container}>
      <Table stickyHeader>
        <TableHead>
          <TableRow>
            <TableCell className={classes.headerCell}>PRODUCED BATCH</TableCell>
            <TableCell className={classes.headerCell}>CONSUMED FROM (PARENTS)</TableCell>
            <TableCell className={classes.headerCell}>YIELD</TableCell>
            <TableCell className={classes.headerCell}>QTY</TableCell>
            <TableCell className={classes.headerCell}>STATUS</TableCell>
            <TableCell className={classes.headerCell}>MFG DATE</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((n) => {
            const s = STATUS[n.status] || STATUS.released;
            return (
              <TableRow
                key={n.id}
                hover
                onClick={() => onSelectBatch && onSelectBatch(n.id)}
                style={{ cursor: onSelectBatch ? 'pointer' : 'default' }}
              >
                <TableCell className={classes.cell} style={{ fontWeight: 700 }}>
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                    <span className={classes.stepBadge}>{n.step}</span>
                    {n.id}
                  </span>
                </TableCell>
                <TableCell className={classes.cell} style={{ color: '#333333' }}>
                  {n.parents && n.parents.length ? n.parents.join(', ') : '— (raw materials)'}
                </TableCell>
                <TableCell className={classes.cell}>{n.yield}%</TableCell>
                <TableCell className={classes.cell}>{n.qty}</TableCell>
                <TableCell className={classes.cell}>
                  <span className={classes.statusChip} style={{ background: s.bg, color: s.text }}>{s.label}</span>
                </TableCell>
                <TableCell className={classes.cell} style={{ color: '#333333' }}>{n.mfgDate || '—'}</TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

export default LineageTable;
